/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Radio, Tabs, TableProps } from 'antd';
import {
  useGetSettlementPerSalesHistory,
  useGetSettlementClientSalesHistory,
  useGetSettlementClientSalesHistoryByPeriod,
  useGetSettlementPartnerSalesHistory,
  useGetSettlementEvidenceHistory,
  useGetEvidencesDetail,
  useGetEvidencesCashReceiptDetail,
  useGetSettlementDepositTransferHistory,
  useGetSettlementCarryOverSalesHistory
} from 'hooks';
import { FlexBoxFullWidthCenter, FlexBoxCenter } from 'styles';

import moment, { Moment } from 'moment';
import EvidenceTaxInvoiceModal from './modal/EvidenceTaxInvoiceModal';
import EvidenceCashReceiptModal from './modal/EvidenceCashReceiptModal';
import {
  //
  getSalesHistoryColumnsMap,
  TSettlementDetail,
  // 증빙 내역
  evidencesColumns,
  attachmentsColumns,
  // 입금/이체 내역
  depositItemColumns
} from './schema';
import { TabsWrapper, FormWrapper, TextUnderline } from './styles';
import PeriodSearchFilter from './PeriodSearchFilter';

const defaultTableProps: TableProps<any> = { locale: { emptyText: '내역이 없습니다' }, pagination: false };

interface SalesSettlementTabsProp {
  salesSettlement: TSettlementDetail;
}

const SalesSettlementTabs = ({ salesSettlement }: SalesSettlementTabsProp) => {
  const navigate = useNavigate();
  const { settlementIdx } = useParams();

  // 거래 내역 탭
  const [activeKey, setActiveKey] = useState<string>('SALES'); // SALES
  const [feeCalcType, setFeeCalcType] = useState<'PER_SALES' | 'CLIENT_SALES' | 'PARTNER_SALES' | 'CARRY_OVER'>('PER_SALES');
  const [searchOption, setSearchOption] = useState<'ALL' | 'DATE'>('ALL');
  const [salesHistoryPage, setSalesHistoryPage] = useState<number>(1);
  const [paymentDate, setPaymentDate] = useState<[Moment, Moment] | null>(null);
  const [clientSearchOption, setClientSearchOption] = useState<'ALL' | 'DATE'>('ALL'); // 회사 별 기간 검색
  const [clientPaymentDate, setClientPaymentDate] = useState<[Moment, Moment] | null>(null);
  const [hasClientSearched, setHasClientSearched] = useState<boolean>(false); // 회사 별 기간 검색 '조회' 실행 여부

  const { mutateAsync: getPerSalesHistory, data: perSales } = useGetSettlementPerSalesHistory(settlementIdx); // 거래 건 별
  const { mutateAsync: getClientSalesHistory, data: clientSales } = useGetSettlementClientSalesHistory(settlementIdx); // 회사 별
  const {
    mutateAsync: getClientSalesByPeriod,
    data: clientSalesByPeriod,
    isLoading: isClientSearching
  } = useGetSettlementClientSalesHistoryByPeriod(settlementIdx); // 회사 별 (기간 필터)
  const { mutateAsync: getPartnerSalesHistory, data: partnerSales } = useGetSettlementPartnerSalesHistory(settlementIdx); // 제휴점 별
  const { mutateAsync: getCarryOverSalesHistory, data: carryOverHistory } = useGetSettlementCarryOverSalesHistory(settlementIdx); // 이월 내역

  // 증빙 내역 탭
  const [isTaxInvoiceModal, setIsTaxInvoiceModal] = useState<boolean>(false);
  const [isCashReceiptModal, setIsCashReceiptModal] = useState<boolean>(false);

  const { mutateAsync: getEvidenceHistory, data: evidenceHistory } = useGetSettlementEvidenceHistory(settlementIdx); // 증빙 내역
  const { mutateAsync: getEvidencesDetail, data: evidenceData } = useGetEvidencesDetail(); // 증빙 내역 상세
  const { mutateAsync: getEvidencesCashReceiptDetail, data: cashReceiptData } = useGetEvidencesCashReceiptDetail(); // 증빙 내역 상세 - 현금 영수증

  // 입금/이채 내역 탭
  const { mutateAsync: getDepositTransferHistory, data: depositTransferHistory } =
    useGetSettlementDepositTransferHistory(settlementIdx); // 정산서 입금 및 이체 내역

  useEffect(() => {
    if (salesSettlement) {
      const status = salesSettlement?.status === 'CONFIRMED' ? 'EVIDENCE' : 'DEPOSIT_TRANSFER';
      const resolvedStatus = salesSettlement?.status === 'READY' ? 'SALES' : status;
      setActiveKey(resolvedStatus);
      setPaymentDate([moment(salesSettlement?.settlementStartDate), moment(salesSettlement?.settlementEndDate)]);
      setClientPaymentDate([moment(salesSettlement?.settlementStartDate), moment(salesSettlement?.settlementEndDate)]);
      setHasClientSearched(false);
    }
  }, [salesSettlement]);

  useEffect(() => {
    if (activeKey === 'SALES') {
      setSalesHistoryPage(1);
      getPerSalesHistory({ page: 1 });
      getClientSalesHistory();
      getPartnerSalesHistory();
      getCarryOverSalesHistory();
    } else if (activeKey === 'EVIDENCE') {
      getEvidenceHistory();
    } else if (activeKey === 'DEPOSIT_TRANSFER') {
      getDepositTransferHistory();
    }
  }, [activeKey]);

  const handleEvidenceCellClick = async (values) => {
    if (!values?.evidenceIdx) return;
    if (values?.evidenceType !== 'CASH_RECEIPT') {
      await getEvidencesDetail(values?.evidenceIdx);
      setIsTaxInvoiceModal(true);
    } else {
      await getEvidencesCashReceiptDetail(values?.evidenceIdx);
      setIsCashReceiptModal(true);
    }
  };

  const handleFeeCalcTypeChange = (value) => setFeeCalcType(value);
  const handleSearchOptionChange = (value) => {
    setSearchOption(value);
    if (value === 'ALL') {
      setSalesHistoryPage(1);
      getPerSalesHistory({ page: 1 });
      getClientSalesHistory();
      getPartnerSalesHistory();
    }
  };
  const handleSearch = () => {
    if (!paymentDate || !paymentDate[0] || !paymentDate[1]) return;
    const [startDate, endDate] = paymentDate;
    setSalesHistoryPage(1);
    getPerSalesHistory({
      page: 1,
      paymentStartDate: startDate.format('YYYY-MM-DD'),
      paymentEndDate: endDate.format('YYYY-MM-DD')
    });
  };

  // 회사 별 기간 검색
  const handleClientSearchOptionChange = (value) => {
    setClientSearchOption(value);
    if (value === 'ALL') {
      setHasClientSearched(false);
      getClientSalesHistory();
    }
  };
  const handleClientSearch = () => {
    if (!clientPaymentDate || !clientPaymentDate[0] || !clientPaymentDate[1]) return;
    const [startDate, endDate] = clientPaymentDate;
    setHasClientSearched(true);
    getClientSalesByPeriod({
      paymentStartDate: startDate.format('YYYY-MM-DD'),
      paymentEndDate: endDate.format('YYYY-MM-DD')
    });
  };

  // 회사 별: '조회'를 실행한 뒤에는 기간 검색 결과(로딩 중엔 빈 값)를, 그 외(조회 전·전체)에는 전체 결과를 표시
  const clientSalesData = clientSearchOption === 'DATE' && hasClientSearched ? clientSalesByPeriod : clientSales;

  const salesHistoryDataMap = {
    PER_SALES: perSales,
    CLIENT_SALES: clientSalesData,
    PARTNER_SALES: partnerSales,
    CARRY_OVER: carryOverHistory
  };
  const salesHistoryColumns = getSalesHistoryColumnsMap(feeCalcType);
  const paginationInfo = { total: salesHistoryDataMap[feeCalcType]?.totalCount, pageSize: 20, current: salesHistoryPage };
  return (
    <TabsWrapper>
      <Tabs activeKey={activeKey} onChange={(value) => setActiveKey(value)}>
        {/*  거래 내역 탭 */}
        <Tabs.TabPane tab="거래 내역" key="SALES">
          <div style={{ display: 'flex' }}>
            <FormWrapper>
              <div className="fieldName">보기 정렬</div>
              <FlexBoxCenter style={{ width: '100%', padding: '0px 16px', border: '1px solid #f0f0f0' }}>
                <Radio.Group value={feeCalcType} onChange={({ target }) => handleFeeCalcTypeChange(target?.value)}>
                  <Radio value="PER_SALES">거래 건 별({perSales?.totalCount || 0})</Radio>
                  <Radio value="CLIENT_SALES">회사 별({clientSalesData?.totalCount || 0})</Radio>
                  <Radio value="PARTNER_SALES">제휴점별({partnerSales?.totalCount || 0})</Radio>
                  <Radio value="CARRY_OVER">
                    <span style={{ color: '#F44336' }}>이월 데이터({carryOverHistory?.totalCount || 0})</span>
                  </Radio>
                </Radio.Group>
              </FlexBoxCenter>
            </FormWrapper>
            {feeCalcType === 'PER_SALES' && (
              <PeriodSearchFilter
                searchOption={searchOption}
                onSearchOptionChange={handleSearchOptionChange}
                paymentDate={paymentDate}
                onPaymentDateChange={setPaymentDate}
                onSearch={handleSearch}
              />
            )}
            {feeCalcType === 'CLIENT_SALES' && (
              <PeriodSearchFilter
                searchOption={clientSearchOption}
                onSearchOptionChange={handleClientSearchOptionChange}
                paymentDate={clientPaymentDate}
                onPaymentDateChange={setClientPaymentDate}
                onSearch={handleClientSearch}
                isSearching={isClientSearching}
              />
            )}
          </div>

          <div style={{ padding: '16px 0px', fontSize: '14px', color: '#666666' }}>
            ※ 26년 이전의 데이터는 매출 내역과 정산 금액이 다를 수 있습니다. (취소 / 이월 / 보상 등 의 사유) / 수수료 계산방식의
            정산 옵션에 따라 정산 예정 금액의 합계가 불일치 할 수 있습니다.
          </div>
          <Table
            columns={salesHistoryColumns}
            dataSource={salesHistoryDataMap[feeCalcType]?.content || []}
            rowClassName={(record) => {
              if (record?.status && record.status !== 0 && record.status !== 9 && record.status !== 8 && record.status !== 6) {
                return 'table-row-error';
              }
              return null;
            }}
            locale={{ emptyText: '내역이 없습니다' }}
            pagination={feeCalcType === 'PER_SALES' ? paginationInfo : false}
            onChange={(value) => {
              setSalesHistoryPage(value.current);
              getPerSalesHistory({ page: value.current });
            }}
          />
        </Tabs.TabPane>

        {/*  증빙 내역 탭 */}
        <Tabs.TabPane tab="증빙 내역" key="EVIDENCE">
          <FlexBoxFullWidthCenter align="initial" direction="column" gap="8px">
            <div style={{ fontSize: '16px', fontWeight: '700' }}>매출(거래 금액) 증빙 내역</div>
            <Table
              columns={evidencesColumns({ onCellClick: handleEvidenceCellClick })}
              dataSource={evidenceHistory?.salesEvidences || []}
              rowClassName={(record) => (record?.totalAmount < 0 ? 'table-row-error' : null)}
              {...defaultTableProps}
            />
            <div style={{ paddingTop: '16px', fontSize: '16px', fontWeight: '700' }}>매입(수수료 등) 증빙 내역</div>
            <Table
              columns={evidencesColumns({ onCellClick: handleEvidenceCellClick })}
              dataSource={evidenceHistory?.purchaseEvidences || []}
              rowClassName={(record) => (record?.totalAmount < 0 ? 'table-row-error' : null)}
              {...defaultTableProps}
            />
            <div>
              <span>※ 내 증빙자료를 모아보고 싶다면 </span>
              <TextUnderline onClick={() => navigate('/evidenceHistory')}>증빙내역</TextUnderline>
              <span>에서 확인해 주세요.</span>
            </div>

            <div style={{ paddingTop: '16px', fontSize: '16px', fontWeight: '700' }}>첨부 자료</div>
            <Table columns={attachmentsColumns} dataSource={evidenceHistory?.attachments || []} {...defaultTableProps} />
          </FlexBoxFullWidthCenter>
        </Tabs.TabPane>

        {/*  증빙 내역 탭 */}
        <Tabs.TabPane tab="입금/이체 내역" key="DEPOSIT_TRANSFER">
          <FlexBoxFullWidthCenter align="initial" direction="column">
            <div style={{ fontSize: '16px', fontWeight: '700' }}>
              입금 내역 (금액: {depositTransferHistory?.depositInfo?.depositAmount?.toLocaleString()})
            </div>
            <div>
              <span>※ 입금 계좌 정보 :&nbsp;</span>
              <span>{depositTransferHistory?.depositInfo?.bankName} /&nbsp;</span>
              <span>{depositTransferHistory?.depositInfo?.accountNumber} /&nbsp;</span>
              <span>{depositTransferHistory?.depositInfo?.accountHolder}</span>
            </div>
            <Table columns={depositItemColumns} dataSource={depositTransferHistory?.depositItems} {...defaultTableProps} />

            {!salesSettlement?.isPayCommissionMerge ? (
              <div>
                <div style={{ paddingTop: '16px', fontSize: '16px', fontWeight: '700' }}>
                  이체 확인 (금액: {depositTransferHistory?.transferInfo?.transferAmount?.toLocaleString()})
                </div>
                <div>
                  <span>※ 입금 계좌 정보 :&nbsp;</span>
                  <span>{depositTransferHistory?.transferInfo?.bankName} /&nbsp;</span>
                  <span>{depositTransferHistory?.transferInfo?.accountNumber} /&nbsp;</span>
                  <span>{depositTransferHistory?.transferInfo?.accountHolder}</span>
                </div>
                <Table columns={depositItemColumns} dataSource={depositTransferHistory?.transferItems} {...defaultTableProps} />
              </div>
            ) : null}
          </FlexBoxFullWidthCenter>
        </Tabs.TabPane>
      </Tabs>

      <EvidenceTaxInvoiceModal
        open={isTaxInvoiceModal}
        evidenceData={evidenceData}
        onCancel={() => setIsTaxInvoiceModal(!isTaxInvoiceModal)}
      />
      <EvidenceCashReceiptModal
        open={isCashReceiptModal}
        evidenceData={cashReceiptData}
        onCancel={() => setIsCashReceiptModal(!isCashReceiptModal)}
      />
    </TabsWrapper>
  );
};

export default SalesSettlementTabs;
